import { getWindowSession } from './helper';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface MigrationState {
  migrationStatus: Record<string, string>;
  setMigrationStatus: (obj: Record<string, string>) => void;
}

interface ConfoMappingState {
  confoMapping: Record<string, string>;
  setConfoMapping: (obj: Record<string, string>) => void;
}

export const useMigrationStore = create<MigrationState>()(
  persist(
    set => ({
      migrationStatus: {},
      setMigrationStatus: (obj: Record<string, string>) =>
        set(state => ({
          migrationStatus: {
            ...state.migrationStatus,
            ...obj,
          },
        })),
    }),
    {
      name: 'migration-storage', // name of the item in storage
    }
  )
);

export const useConfoStore = create<ConfoMappingState>()(
  persist(
    set => ({
      confoMapping: {},
      setConfoMapping: (obj: Record<string, string>) =>
        set(state => ({
          confoMapping: {
            ...state.confoMapping,
            ...obj,
          },
        })),
    }),
    {
      name: 'confo-storage', // name of the item in storage
    }
  )
);

/**
 * set the UXL Migration Status in the session for the specific property
 */
export const setUXLMigrationStatus = (headers: Headers, propertyId?: string) => {
  const sessionObject = propertyId ? '' : getWindowSession();
  const setMigrationStatus = useMigrationStore.getState().setMigrationStatus;
  const migrationStatus = headers.get('uxl-migration-status');

  if (migrationStatus) {
    const upperCasePropertyId = (
      propertyId ?? sessionObject?.cacheData?.data?.AriesReservation?.propertyId
    )?.toUpperCase();
    setMigrationStatus({ [upperCasePropertyId]: migrationStatus });
  }
};

/**
 * get the UXL Migration Status in the session for the specific property
 *
 * @returns {String}
 */
export const getUXLMigrationStatus = (propertyId?: string) => {
  const sessionObject = propertyId ? '' : getWindowSession();
  const migrationStatus = useMigrationStore.getState().migrationStatus;
  const upperCasePropertyId = (
    propertyId ?? sessionObject?.cacheData?.data?.AriesReservation?.propertyId
  )?.toUpperCase();
  return migrationStatus[upperCasePropertyId] ?? '';
};

/**
 * set the the Migrated confirmation number in memory
 * @param headers
 */
export const setMigratedConfo = (originalConfirmation: string, migratedConfirmationNumber?: string) => {
  const setConfoMapping = useConfoStore.getState().setConfoMapping;
  if (migratedConfirmationNumber) {
    setConfoMapping({ [originalConfirmation]: migratedConfirmationNumber });
  } else {
    setConfoMapping({ [originalConfirmation]: originalConfirmation });
  }
};

/**
 * get the Migrated Confirmation Number for migrated reservations
 *
 * @returns {string}
 */
export const getMigratedConfo = (confirmationNumber: string) => {
  const confoMapping = useConfoStore.getState().confoMapping;
  return confoMapping[confirmationNumber] ?? confirmationNumber;
};
